import { anno_isUiLabelItem, anno_isUiRegionItem } from 'utils/cfUiItems/guards';
import { AnnoUiFrameItem, AnnoUiItemWithFrame, AnnoUiLabelItem, AnnoUiRegionItem } from 'utils/cfUiItems/annotations';
import { getShapeProps } from 'utils/predictions/predictions';
import { AnnotationMSTByType, TIAnnotationMST } from 'modules/Annotations/store/annotation';
import { ColorItem } from 'api/typeConstructors_deprecated/modelOutputs';
import { hasConcepts, hasRegionsWithConcepts } from 'components/ModelOutputRenderer/components/utils';
import { AnnotationItem } from 'modules/Annotations/store/helpers';
import { ClassificationAnnotationMap, RegionAnnotationMap } from 'modules/Annotations/store/annotationsStore';
import objectPath from 'object-path';
import { cloneDeep } from 'lodash';
import { makeUID } from 'utils/strings/strings';

/**
 * Pretty print concept value at the cost of losing accuracy.
 */
export function prettyPrintDecimal(numberOfDigits: number, float: number): string {
  return (Math.trunc(float * 10 ** numberOfDigits) / 10 ** numberOfDigits).toFixed(numberOfDigits);
}

export function extractColorData(item: ColorItem): { name: string; value: string } {
  return { name: item.color.w3c.name, value: item.color.w3c.hex };
}

export function getConceptsFromAnnotationData(annotationData: CF.API.TOutputData): CF.API.Concept[] {
  if (hasConcepts(annotationData)) {
    return annotationData.concepts || [];
  } else if (hasRegionsWithConcepts(annotationData)) {
    return (annotationData.regions || []).flatMap(({ data }) => data.concepts || []);
  }

  return [];
}
type UiAnnoTypes = 'bbox' | 'polygon' | 'concept' | 'imagemask' | 'unsupported' | 'frame' | 'point';

type Concept = CF.API.Concept;
type Region = CF.API.RegionsEntity;
type Concepts = Concept[];
type Regions = Region[];
type Frame = CF.API.FramesEntity;

type AnnotationByType = {
  byType: TAnnoDataSum;
  annotation?: TIAnnotationMST;
};

function createEmptyMap<T>(): Record<UiAnnoTypes, T[]> {
  const obj = {
    bbox: [],
    polygon: [],
    concept: [],
    imagemask: [],
    unsupported: [],
    frame: [],
    point: [],
  };
  return obj;
}

const prepareAnnotationTypes = (itemMap: Record<UiAnnoTypes, AnnoUiItemWithFrame[]>, annotationByType: AnnotationByType) => {
  const { byType, annotation } = annotationByType;
  if ('bbox' in byType) {
    itemMap.bbox = itemMap.bbox.concat(prepareRegionList(byType.bbox, annotation));
  }

  if ('polygon' in byType) {
    itemMap.polygon = itemMap.polygon.concat(prepareRegionList(byType.polygon, annotation));
  }
  if ('imagemask' in byType) {
    itemMap.imagemask = itemMap.imagemask.concat(prepareRegionList(byType.imagemask, annotation));
  }

  if ('concept' in byType) {
    itemMap.concept = itemMap.concept.concat(prepareConceptList(byType.concept, annotation));
  }
  if ('frame' in byType) {
    itemMap.frame = itemMap.frame.concat([byType.frame as unknown as AnnoUiFrameItem<TIAnnotationMST>]);
  }

  return itemMap;
};

const buildAnnotationMap = (itemMap: Record<UiAnnoTypes, AnnoUiItemWithFrame[]>, outputData: CF.API.TOutputData, annotation?: TIAnnotationMST) => {
  const annotationByType = splitByType(outputData, annotation);
  if (annotationByType) {
    itemMap = prepareAnnotationTypes(itemMap, annotationByType);
  }
  return itemMap;
};

// guarantees we never send an unsupported or empty list to component
export function viewAnnotationList(annotations: TIAnnotationMST[]): AnnotationMSTByType {
  let itemMap = createEmptyMap<AnnoUiItemWithFrame>();

  annotations.forEach((currentAnnotationEntity) => {
    itemMap = buildAnnotationMap(itemMap, currentAnnotationEntity.data, currentAnnotationEntity);
  });
  return itemMap as unknown as AnnotationMSTByType;
}

function prepareRegionList(regionList: CF.API.RegionsEntity[], annotation?: TIAnnotationMST): AnnoUiRegionItem<TIAnnotationMST>[] {
  return regionList
    .map((region) => {
      const internalId = generateInternalAnnotationId({
        id: annotation?.id || makeUID(16),
        regionId: region.id,
        conceptId: region.data.concepts?.[0]?.id || '',
        worker: annotation?.worker || {},
        taskId: annotation?.task_id || '',
      });

      // transform concept to UiLabelItem
      return region.data.concepts?.length
        ? ({
            key: region.id,
            concepts: region.data.concepts,
            regionData: getShapeProps(region),
            annotation: { ...annotation, internalId },
          } as AnnoUiRegionItem<TIAnnotationMST>)
        : 'text' in region.data
        ? {
            key: region.id,
            regionData: getShapeProps(region),
            annotation,
            text: region.data.text,
          }
        : false;
    })
    .filter<AnnoUiRegionItem<TIAnnotationMST>>(
      (x: false | AnnoUiRegionItem<TIAnnotationMST>): x is AnnoUiRegionItem<TIAnnotationMST> => x && anno_isUiRegionItem(x),
    );
}

function prepareConceptList(conceptList: CF.API.Concept[], annotation?: TIAnnotationMST): AnnoUiLabelItem<TIAnnotationMST>[] {
  return (
    (conceptList as CF.API.Concept[])
      // transform concept to UiLabelItem
      .map((concept) => ({
        key: concept.id,
        concept,
        annotation,
      }))
      .filter(anno_isUiLabelItem)
  );
}

type TAnnoDataSum = { concept: Concepts } | { bbox: Regions } | { polygon: Regions } | { imagemask: Regions } | { frame: Frame } | { point: Frame };

// because it is theoretically possible (however rare) for an annotation to have multiple regions,
// we split the regions by type, and try to treat each region as an individual entity that still retains its parent annotation's identity
function splitByType(annotationData: CF.API.TOutputData, annotation?: TIAnnotationMST): AnnotationByType | undefined {
  if (!annotationData) return undefined;
  if ('regions' in annotationData && annotationData.regions?.[0]) {
    const key =
      'bounding_box' in (annotationData.regions[0]?.region_info || {})
        ? ('bbox' as const)
        : 'polygon' in (annotationData.regions[0]?.region_info || {})
        ? ('polygon' as const)
        : ('imagemask' as const);

    return { byType: { [key]: annotationData.regions } as Record<'bbox' | 'polygon' | 'imagemask', Regions>, annotation };
  } else if ('concepts' in annotationData && annotationData.concepts) {
    return { byType: { concept: annotationData.concepts }, annotation };
  } else if ('frames' in annotationData && annotationData.frames?.[0]) {
    const frame = annotationData.frames[0];
    if (!frame.metadata) {
      return { byType: { frame: { ...frame, metadata: annotationData.metadata } }, annotation };
    } else {
      return { byType: { frame }, annotation };
    }
  }

  // unsupported
  return undefined;
}

const SEPARATOR_KEY = '###';

type InternalDecomposedAnnotationId = {
  id: string;
  regionId: string;
  conceptId: string;
  worker?: CF.API.Worker;
  taskId: string;
};

export const generateInternalAnnotationId = ({
  id = '',
  regionId = '',
  conceptId = '',
  worker = {},
  taskId = '',
}: InternalDecomposedAnnotationId): string => {
  const workerWorkflowId = worker.workflow?.id || '';
  const workerWorkflowVersionId = worker.workflow?.version?.id || '';
  const workerModelId = worker.model?.id || '';
  const workerModelVersionId = worker.model?.model_version?.id || '';
  const workerUserId = worker.user?.id || '';

  return [id, regionId, conceptId, workerWorkflowId, workerWorkflowVersionId, workerModelId, workerModelVersionId, workerUserId, taskId].join(
    SEPARATOR_KEY,
  );
};

const DEFAULT_DECOMPOSED_ANNOTATION_ID = {
  id: '',
  regionId: '',
  conceptId: '',
  workerWorkflowId: '',
  workerWorkflowVersionId: '',
  workerModelId: '',
  workerModelVersionId: '',
  workerUserId: '',
  taskId: '',
};
export const decomposeInternalAnnotationId = (internalId?: string) => {
  if (!internalId) {
    return DEFAULT_DECOMPOSED_ANNOTATION_ID;
  }

  const values = internalId.split(SEPARATOR_KEY);

  if (values.length !== Object.keys(DEFAULT_DECOMPOSED_ANNOTATION_ID).length) {
    console.error(`DecomposeInternalAnnotationId FAILURE: ${internalId} is not valid`);
    return DEFAULT_DECOMPOSED_ANNOTATION_ID;
  }

  const [id, regionId, conceptId, workerWorkflowId, workerWorkflowVersionId, workerModelId, workerModelVersionId, workerUserId, taskId] = values;

  return {
    id,
    regionId,
    conceptId,
    workerWorkflowId,
    workerWorkflowVersionId,
    workerModelId,
    workerModelVersionId,
    workerUserId,
    taskId,
  };
};

// Helper Function to determine if a concept group contains annotations from the provided user or has multiple items
const _isUserAnnotatedOrMultiple = <T extends AnnoUiLabelItem>(group: { key: string; concepts: T[] }, userId: string): boolean =>
  group.concepts.some((item) => item.annotation?.worker?.user?.id === userId) || group.concepts.length > 1;

// Helper Function to compare and sort concept groups based on their name
const _sortByConceptName = <T extends AnnoUiLabelItem>(a: { key: string; concepts: T[] }, b: { key: string; concepts: T[] }) =>
  (a.concepts[0]?.concept?.name || '').localeCompare(b.concepts[0]?.concept?.name || '');

//  Helper Function to group concepts by their ID
const _groupByConceptID = (
  _concepts: AnnoUiLabelItem<TIAnnotationMST>[],
): Record<string, { key: string; concepts: AnnoUiLabelItem<TIAnnotationMST>[] }> => {
  const grouped: Record<string, { key: string; concepts: AnnoUiLabelItem<TIAnnotationMST>[] }> = {};
  _concepts.forEach((x) => {
    const key = 'concept' in x && x.concept && x.concept.id ? `${x.concept.id}##cf##${x.concept.name}` : 'N/A##cf##N/A';
    if (!grouped[key]) {
      grouped[key] = { key, concepts: [] };
    }
    grouped[key].concepts.push(x);
  });
  return grouped;
};

export function groupAndOrderConceptsAnnotationsGroup(
  _conceptAnnotations: AnnoUiLabelItem<TIAnnotationMST>[],
  userId: string,
): { key: string; concepts: AnnoUiLabelItem<TIAnnotationMST>[] }[] {
  // Step 1: Group Concepts by ID
  const grouped = _groupByConceptID(_conceptAnnotations);
  const groupedArray = Object.values(grouped);

  // Step 2: Separate Groups based on User Annotation or Item Count
  const userOrMultipleItems = groupedArray.filter((group) => _isUserAnnotatedOrMultiple(group, userId));
  const remainingItems = groupedArray.filter((group) => !_isUserAnnotatedOrMultiple(group, userId));

  // Step 3: Sort the Identified Groups

  // 3.1. Sort user annotated or multiple item groups by size (number of items). If sizes are the same, sort by concept name
  userOrMultipleItems.sort((a, b) => (a.concepts.length === b.concepts.length ? _sortByConceptName(a, b) : b.concepts.length - a.concepts.length));

  // 3.2. Sort remaining items by their value. If values are the same, sort by concept name
  remainingItems.sort((a, b) => {
    const aValue = a.concepts[0].concept.value ?? 0;
    const bValue = b.concepts[0].concept.value ?? 0;
    return aValue !== bValue ? bValue - aValue : _sortByConceptName(a, b);
  });
  // Step 4: Return the Final Sorted and Grouped Array
  return [...userOrMultipleItems, ...remainingItems];
}

export const getUnacceptedRegionSuggestions = (regionAnnotationMap: RegionAnnotationMap): AnnotationItem[] => {
  const result: AnnotationItem[] = [];
  Array.from(regionAnnotationMap.values()).forEach((entry) => {
    if (!entry) return;
    entry.regionMap.forEach((annotations) => {
      annotations.forEach((annotation) => {
        if (annotation.isSuggestion) {
          result.push(annotation);
        }
      });
    });
  });
  return result;
};

export const getUnacceptedClassificationSuggestions = (classificationMap: ClassificationAnnotationMap): ClassificationAnnotationMap => {
  const result: ClassificationAnnotationMap = new Map();
  for (const [key, annotations] of classificationMap.entries()) {
    const unacceptedAnnotations = annotations.filter((a) => a && !!a.isSuggestion);
    if (unacceptedAnnotations.length > 0) {
      result.set(key, unacceptedAnnotations);
    }
  }

  return result;
};

export const updateAnnotationProperties = (
  annotation: CF.API.Annotations.Annotation,
  // TODO: Create a TS utility to check that the keys and values are valid for the provided object.
  annotationKeyValueMap: { [key: string]: unknown },
): CF.API.Annotations.Annotation => {
  // To preserve immutability.
  const annotationToUpdate = cloneDeep(annotation);

  for (const [key, value] of Object.entries(annotationKeyValueMap)) {
    objectPath.set(annotationToUpdate, key, value);
  }

  return annotationToUpdate;
};
